import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import {
  dayNames, toForm, readForm, hourOptions, hourLabel, minuteOptions, minuteLabel, dateCount, weekSummary,
  periodLabel, todayValue, newHoliday, openAllDays, usualTimes, timeLabel,
} from './doorScheduleForm';

const Card = styled.section`
  margin: 40px 0 30px; padding-bottom: 24px; border: 1px solid #e6e9ee; border-radius: 5px; background: #fff;
  color: #141414; font-size: 14px; line-height: 1.5;
  * { box-sizing: border-box; }
`;
const Head = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 22px 26px 0;
  h2 { margin: 0; font-size: 16px; font-weight: 400; color: #000; border-left: 6px solid #1a73e9; padding-left: 10px; }
  p { margin: 6px 0 0 16px; font-size: 13px; color: #646464; }
`;
const Summary = styled.dl`
  display: grid; grid-template-columns: 100px minmax(0, 1fr); gap: 8px 16px; margin: 18px 26px 0 42px;
  dt { color: #646464; } dd { margin: 0; }
  .sep { color: #c3c8cf; margin: 0 6px; }
  .muted { color: #9aa3af; }
  @media(max-width: 600px) { grid-template-columns: 1fr; gap: 2px; margin-left: 26px; dd { margin-bottom: 10px; } }
`;
const Warn = styled.p`margin: 18px 26px 0; padding: 12px 16px; border-radius: 5px; background: #fff8e6; color: #7a5a00; font-size: 14px;`;
const Message = styled.div`
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin: 14px 26px 0 42px;
  color: ${p => p.$error ? '#c44242' : '#247449'}; font-size: 14px;
`;
const Button = styled.button`
  border: 1px solid ${p => p.primary ? '#1a73e9' : '#c9ced6'}; border-radius: 5px; font-family: inherit;
  background: ${p => p.primary ? '#1a73e9' : '#fff'}; color: ${p => p.primary ? '#fff' : '#141414'};
  min-height: 40px; padding: 8px 18px; cursor: pointer; font-size: 15px; white-space: nowrap;
  &:hover:not(:disabled) { filter: brightness(.97); }
  &:focus-visible { outline: 2px solid #1a73e9; outline-offset: 2px; }
  &:disabled { cursor: default; background: #edf0f4; color: #9aa3af; border-color: #edf0f4; }
`;
const LinkButton = styled.button`
  border: 0; background: none; padding: 12px 16px; color: #1a73e9; font: inherit; font-size: 14px; cursor: pointer; text-decoration: underline;
  &:disabled { color: #9aa3af; cursor: default; }
`;

const Body = styled.div`
  color: #141414; font-size: 15px; line-height: 1.5;
  * { box-sizing: border-box; }
  h3 { margin: 0 0 4px; font-size: 17px; }
  h3 small { font-size: 13px; font-weight: 400; color: #868e96; margin-left: 6px; }
  .desc { margin: 0 0 14px; font-size: 14px; color: #646464; }
  .desc b { color: #141414; }
  select, input[type=date] { font: inherit; font-size: 15px; color: #141414; background: #fff; border: 1px solid #c9ced6; border-radius: 4px; padding: 7px 8px; min-height: 40px; }
  select:disabled, input:disabled { background: #f1f3f5; color: #9a9a9a; }
  select[aria-invalid=true] { border-color: #e03131; background: #fff5f5; }
  select:focus-visible, input:focus-visible { outline: 2px solid #1a73e9; outline-offset: 2px; }
`;
const Part = styled.section`padding: 6px 0 26px; & + & { border-top: 1px solid #e6e9ee; padding-top: 22px; }`;
const Callout = styled.p`margin: 0 0 18px; padding: 12px 16px; border-radius: 5px; background: ${p => p.$error ? '#fdecec' : '#fff8e6'}; color: ${p => p.$error ? '#c44242' : '#7a5a00'}; font-size: 14px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap;`;
const Choices = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 12px; @media(max-width: 640px) { grid-template-columns: 1fr; }`;
const Choice = styled.label`
  display: flex; gap: 12px; align-items: flex-start; padding: 16px; border-radius: 8px; cursor: pointer;
  border: 2px solid ${p => p.$on ? '#1a73e9' : '#e1e5eb'}; background: ${p => p.$on ? '#f3f8ff' : '#fff'};
  input { width: 20px; height: 20px; margin: 2px 0 0; accent-color: #1a73e9; flex-shrink: 0; }
  strong { display: block; font-size: 16px; }
  span { display: block; margin-top: 2px; font-size: 13px; color: #646464; }
`;
const Rows = styled.div`margin-top: 16px; border: 1px solid #e6e9ee; border-radius: 8px;`;
const RuleLine = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 10px 16px; padding: ${p => p.$plain ? '12px 0 0' : '12px 16px'};
  & + & { border-top: 1px solid #eef0f3; }
  .who { width: 92px; flex-shrink: 0; font-weight: 700; }
  .who span { display: block; font-size: 12px; font-weight: 400; color: #868e96; }
  .hint { font-size: 13px; color: #868e96; }
  .err { flex-basis: 100%; margin: 0; font-size: 13px; color: #c44242; }
`;
const Pills = styled.div`
  display: inline-flex; border: 1px solid #c9ced6; border-radius: 6px; overflow: hidden;
  label { position: relative; padding: 8px 14px; font-size: 14px; cursor: pointer; background: #fff; color: #343a40; white-space: nowrap; }
  label + label { border-left: 1px solid #c9ced6; }
  label.on { background: #1a73e9; color: #fff; }
  label:focus-within { outline: 2px solid #1a73e9; outline-offset: -2px; }
  input { position: absolute; opacity: 0; width: 1px; height: 1px; margin: 0; }
`;
const Times = styled.div`
  display: inline-flex; align-items: center; flex-wrap: wrap; gap: 8px; color: #646464; font-size: 14px;
  .clock { display: inline-flex; gap: 4px; white-space: nowrap; }
`;
const HolidayItem = styled.div`
  border: 1px solid ${p => p.$invalid ? '#f1b5b5' : '#e6e9ee'}; border-radius: 8px; padding: 14px 16px; margin-bottom: 10px;
  .dates { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 10px; }
  .dates label { display: flex; align-items: center; gap: 6px; font-size: 14px; color: #646464; }
  .count { font-size: 14px; color: #1a73e9; }
  .past { font-size: 12px; color: #646464; background: #f1f3f5; padding: 2px 8px; border-radius: 3px; }
  .remove { margin-left: auto; }
`;
const Small = styled.p`margin: 10px 0 0; font-size: 13px; color: #868e96;`;
const Actions = styled.div`
  display: flex; align-items: center; gap: 8px; width: 100%; padding: 8px 8px 8px 16px;
  .hint { margin-right: auto; font-size: 14px; color: ${p => p.$error ? '#c44242' : '#646464'}; }
`;

const ruleChoices = [[0, '24시간'], [1, '시간 정하기'], [2, '쉬는 날']];
const holidayChoices = [[1, '시간 정하기'], [2, '쉬는 날']];

function RuleRow({ title, sub, lead, rule, choices = ruleChoices, error, disabled, plain, onChange }) {
  const name = useRef(`door-rule-${Math.random().toString(36).slice(2)}`).current;
  const invalid = !!error && rule.startMinute >= rule.endMinute;
  const clock = (key, text) => {
    const hour = Math.floor(rule[key] / 60), minute = rule[key] % 60;
    const set = (h, m) => onChange({ [key]: h * 60 + (h === 24 ? 0 : m) });
    return <span className="clock">
      <select aria-label={`${title} ${text} 시`} value={hour} disabled={disabled} aria-invalid={invalid} onChange={e => set(Number(e.target.value), minute)}>
        {hourOptions(key === 'startMinute' ? 'start' : 'end').map(h => <option key={h} value={h}>{hourLabel(h)}</option>)}
      </select>
      <select aria-label={`${title} ${text} 분`} value={minute} disabled={disabled || hour === 24} aria-invalid={invalid} onChange={e => set(hour, Number(e.target.value))}>
        {minuteOptions(minute).map(m => <option key={m} value={m}>{minuteLabel(m)}</option>)}
      </select>
    </span>;
  };
  return <RuleLine $plain={plain}>
    {lead ? <span>{lead}</span> : <div className="who">{title}{sub && <span>{sub}</span>}</div>}
    <Pills role="radiogroup" aria-label={`${title} 출입 방식`}>
      {choices.map(([mode, text]) => <label key={mode} className={rule.mode === mode ? 'on' : ''}>
        <input type="radio" name={name} checked={rule.mode === mode} disabled={disabled} onChange={() => onChange({ mode })} />{text}
      </label>)}
    </Pills>
    {rule.mode === 1 && <Times>{clock('startMinute', '시작')}<span>부터</span>{clock('endMinute', '종료')}<span>까지</span></Times>}
    {rule.mode === 0 && <span className="hint">하루 종일 출입할 수 있어요</span>}
    {rule.mode === 2 && <span className="hint">하루 종일 출입할 수 없어요</span>}
    {error && <p className="err" role="alert">{error}</p>}
  </RuleLine>;
}

const DoorSchedule = ({ info, loading, saving, error, needsReload, onReload, onSave }) => {
  const [form, setForm] = useState(null);
  const counter = useRef(0);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const busy = loading || saving;
  const result = form && readForm(form);
  const baseline = info && readForm(toForm(info.settings)).settings;
  const dirty = !!result && JSON.stringify(result.settings) !== JSON.stringify(baseline);
  const canSave = !!result && !!result.settings && (dirty || !!info.requiresApply) && !needsReload && !busy;

  const close = () => { if (!saving) setForm(null); };
  const reload = () => { setForm(null); onReload(); };
  const save = async () => {
    if (!canSave) return;
    const ok = await onSave(result.settings);
    if (ok && alive.current) setForm(null);
  };
  const setMode = mode => setForm(v => ({ ...v, mode, days: mode === 1 ? openAllDays(v.days) : v.days }));
  // Rows that cover several days (평일) keep those days identical.
  const setRule = (indexes, patch) => setForm(v => {
    const next = { ...v.days[indexes[0]], ...patch };
    return { ...v, days: v.days.map((d, i) => indexes.includes(i) ? { ...next } : d) };
  });
  const toggleSplit = () => setForm(v => v.split ? { ...v, split: false, days: v.days.map((d, i) => i < 5 ? { ...v.days[0] } : d) } : { ...v, split: true });
  const setHoliday = (key, patch) => setForm(v => ({
    ...v, holidays: v.holidays.map(h => {
      if (h.key !== key) return h;
      const next = { ...h, ...patch, ...(patch.mode === 1 && h.mode !== 1 ? usualTimes(v, h.startDate) : {}) };
      // Picking only the first day means a one-day closure.
      if (patch.startDate && (!next.endDate || next.endDate < patch.startDate)) next.endDate = patch.startDate;
      return next;
    }),
  }));
  const addHoliday = () => setForm(v => ({ ...v, holidays: [...v.holidays, newHoliday(`h_${Date.now()}_${++counter.current}`)] }));
  const removeHoliday = key => setForm(v => ({ ...v, holidays: v.holidays.filter(h => h.key !== key) }));

  const today = todayValue();
  const rows = form && (form.split
    ? dayNames.map((name, i) => ({ title: `${name}요일`, sub: '', indexes: [i] }))
    : [{ title: '평일', sub: '월~금', indexes: [0, 1, 2, 3, 4] }, { title: '토요일', sub: '', indexes: [5] }, { title: '일요일', sub: '', indexes: [6] }]);
  const upcoming = info ? info.settings.holidays.filter(h => h.endDate >= today) : [];
  const badRow = result && result.error && (form.mode === 1 && rows.find(r => r.indexes.some(i => result.dayErrors[i])));
  const footerHint = !result ? '' : form.mode === null ? '먼저 위에서 ‘24시간 언제나’나 ‘요일별로 시간 정하기’ 중 하나를 골라 주세요.' : result.error ? (badRow ? `${badRow.title} 시간을 확인해 주세요.` : '특별한 날의 빨간 표시를 확인해 주세요.')
    : needsReload ? '최신 설정을 다시 불러온 뒤 수정해 주세요.'
    : !dirty && !info.requiresApply ? '바뀐 내용이 없어요.' : '저장하면 모든 출입문에 바로 적용돼요.';

  return <Card aria-label="회원 출입 가능 시간">
    <Head>
      <div><h2>회원 출입 가능 시간</h2><p>모든 출입문, 모든 회원에게 똑같이 적용돼요.</p></div>
      <Button onClick={() => setForm({ ...toForm(info.settings), ...(info.requiresApply ? { mode: null } : {}) })} disabled={!info || busy || needsReload}>출입 시간 변경</Button>
    </Head>
    {error && !form && <Message $error role="alert">{error}{needsReload && <Button onClick={onReload} disabled={busy}>최신 내용 불러오기</Button>}</Message>}
    {loading && !info && <Message role="status">출입 시간을 불러오는 중이에요.</Message>}
    {info && !needsReload && (info.requiresApply
      ? <Warn>출입문 장비에 이 화면에서 보여드릴 수 없는 출입 시간이 설정되어 있어요. 지금 설정은 그대로 작동하고 있어요. 바꾸고 싶을 때만 ‘출입 시간 변경’을 눌러 정하고 저장하세요.</Warn>
      : <Summary aria-label="현재 출입 시간">
        <dt>평소</dt>
        <dd>{weekSummary(info.settings).map((g, i) => <React.Fragment key={i}>{i > 0 && <span className="sep">·</span>}{g.days} {g.text}</React.Fragment>)}</dd>
        <dt>특별한 날</dt>
        <dd>{upcoming.length ? <>{upcoming.slice(0, 3).map(h => <div key={h.key}>{periodLabel(h.startDate, h.endDate)}<span className="sep">·</span>{h.mode === 2 ? '쉬는 날' : `${timeLabel(h.startMinute)}~${timeLabel(h.endMinute)}만 출입`}</div>)}{upcoming.length > 3 && <div className="muted">외 {upcoming.length - 3}건</div>}</> : <span className="muted">없음</span>}</dd>
      </Summary>)}

    <Dialog open={!!form} onClose={(e, reason) => reason !== 'backdropClick' && close()} aria-labelledby="door-schedule-title" maxWidth="md" fullWidth scroll="paper" PaperProps={{ style: { maxWidth: 1040 } }}>
      <DialogTitle id="door-schedule-title">회원 출입 가능 시간 변경</DialogTitle>
      {form && <DialogContent dividers><Body>
        {error && <Callout $error role="alert">{error}{needsReload && <Button onClick={reload} disabled={busy}>최신 내용 불러오기</Button>}</Callout>}
        {info.requiresApply && !error && <Callout>지금 출입문 장비에 있는 시간은 이 화면에서 보여드릴 수 없어요. 저장하기 전까지는 아무것도 바뀌지 않아요. 방식을 고르고 저장하면 그때부터 장비의 기존 시간 대신 이 내용으로 바뀌어요.</Callout>}
        <Part aria-label="평소 출입 시간">
          <h3>평소에 언제 출입할 수 있나요?</h3>
          <p className="desc">회원이 얼굴·QR 등으로 인증해서 문을 열 수 있는 시간이에요. 이 시간 밖에는 인증해도 문이 열리지 않아요.</p>
          <Choices role="radiogroup" aria-label="출입 시간 방식">
            <Choice $on={form.mode === 0}><input type="radio" name="door-schedule-mode" checked={form.mode === 0} disabled={busy} onChange={() => setMode(0)} />
              <div><strong>24시간 언제나</strong><span>시간 제한 없이 항상 출입할 수 있어요.</span></div></Choice>
            <Choice $on={form.mode === 1}><input type="radio" name="door-schedule-mode" checked={form.mode === 1} disabled={busy} onChange={() => setMode(1)} />
              <div><strong>요일별로 시간 정하기</strong><span>예) 평일 06:00~23:00, 일요일은 쉬는 날</span></div></Choice>
          </Choices>
          {form.mode === 1 && <Rows>
            {rows.map(r => <RuleRow key={r.title} title={r.title} sub={r.sub} rule={form.days[r.indexes[0]]} disabled={busy}
              error={r.indexes.map(i => result.dayErrors[i]).find(Boolean)} onChange={patch => setRule(r.indexes, patch)} />)}
            <LinkButton type="button" disabled={busy} onClick={toggleSplit}>{form.split ? '평일(월~금)을 월요일과 같게 맞추기' : '요일마다 다르게 정하기'}</LinkButton>
          </Rows>}
        </Part>
        <Part aria-label="특별한 날">
          <h3>특별한 날 (명절·휴관·단축 운영)<small>필요할 때만</small></h3>
          <p className="desc">설날·추석·여름휴가처럼 <b>특정 날짜</b>에만 문을 닫거나 시간을 줄일 때 추가하세요.<br />매주 쉬는 요일(예: 일요일)은 위에서 ‘쉬는 날’로 정하면 되고, 여기에 넣지 않아도 돼요.</p>
          {form.holidays.map((h, i) => {
            const n = i + 1, count = dateCount(h.startDate, h.endDate);
            return <HolidayItem key={h.key} $invalid={!!result.holidayErrors[h.key]} aria-label={`특별한 날 ${n}`}>
              <div className="dates">
                <label>시작일<input type="date" aria-label={`특별한 날 ${n} 시작일`} value={h.startDate} disabled={busy} onChange={e => setHoliday(h.key, { startDate: e.target.value })} /></label>
                <span>~</span>
                <label>종료일<input type="date" aria-label={`특별한 날 ${n} 종료일`} value={h.endDate} min={h.startDate || undefined} disabled={busy} onChange={e => setHoliday(h.key, { endDate: e.target.value })} /></label>
                {count && <span className="count">{count}</span>}
                {h.endDate && h.endDate < today && <span className="past">지난 날짜</span>}
                <Button className="remove" aria-label={`특별한 날 ${n} 삭제`} disabled={busy} onClick={() => removeHoliday(h.key)}>삭제</Button>
              </div>
              <RuleRow plain lead={h.startDate && h.endDate && h.startDate !== h.endDate ? '이 기간에는' : '이 날은'} title={`특별한 날 ${n}`} rule={h} choices={holidayChoices} disabled={busy} error={result.holidayErrors[h.key]} onChange={patch => setHoliday(h.key, patch)} />
            </HolidayItem>;
          })}
          <Button disabled={busy || form.holidays.length >= 32} onClick={addHoliday}>+ 날짜 추가</Button>
          <Small>하루만 쉬는 날은 시작일만 고르면 돼요. 등록한 날짜는 한 번만 적용되고 매년 반복되지 않아요.</Small>
        </Part>
      </Body></DialogContent>}
      <DialogActions>
        <Actions $error={!!(result && result.error && form.mode !== null)}>
          <span className="hint">{footerHint}</span>
          <Button onClick={close} disabled={saving}>취소</Button>
          <Button primary onClick={save} disabled={!canSave}>{saving ? '저장 중…' : '저장'}</Button>
        </Actions>
      </DialogActions>
    </Dialog>
  </Card>;
};
export default DoorSchedule;
